import { Box } from '@radix-ui/themes';
import {
    Bar,
    BarProps,
    BarShapeProps,
    BarChart as ReBarChart,
    XAxis,
    YAxis,
} from 'recharts';
import { ChartItem, ValueFormatter } from '.';

import styles from './Charts.module.css';
import { Dimensions, useMeasure } from '@common/hooks/useMeasure';

interface BarChartProps extends BarProps {
    data: ChartItem[];
    dataKey: string;
    hoveredName?: string;
    onHover?: (name: string | undefined) => void;
    valueFormatter?: ValueFormatter;
}

interface HoverableBarShapeProps extends BarShapeProps {
    onHover?: (name: string | undefined) => void;
    containerRect: Dimensions;
    valueFormatter?: ValueFormatter;
}

const InactiveBarShape = (props: HoverableBarShapeProps) => {
    const {
        x,
        y,
        width,
        height,
        fill,
        style,
        className,
        radius,
        name,
        onHover,
    } = props;

    const radiusArray = Array.isArray(radius)
        ? radius
        : Array.from({ length: 4 }).map(() => radius ?? 0);

    // 0. Starting Point
    const startX = x;
    const startY = y + radiusArray[0];
    // 0.5. TOP/LEFT CORNER
    const quadratic1X = startX;
    const quadratic1Y = startY - radiusArray[0];
    const point1X = quadratic1X + radiusArray[0];
    const point1Y = quadratic1Y;

    // 1. TOP SEGMENT
    const topLength = startX + width - radiusArray[1];
    // 1.5. TOP/RIGHT CORNER
    const quadratic2X = topLength + radiusArray[1];
    const quadratic2Y = point1Y;
    const point2X = quadratic2X;
    const point2Y = quadratic2Y + radiusArray[1];

    // 2. RIGHT SEGMENT
    const rightLength = y + height - radiusArray[2];
    // 2.5 BOTTOM/RIGHT CORNER
    const quadratic3X = point2X;
    const quadratic3Y = rightLength + radiusArray[2];
    const point3X = quadratic3X - radiusArray[2];
    const point3Y = quadratic3Y;

    // 3. BOTTOM SEGMENT
    const bottomLength = startX + radiusArray[3];
    // 3.5 BOTTOM/LEFT CORNER
    const quadratic4X = bottomLength - radiusArray[3];
    const quadratic4Y = point3Y;
    const point4X = quadratic4X;
    const point4Y = quadratic4Y - radiusArray[3];

    return (
        <path
            d={`M${startX},${startY}
                Q${quadratic1X},${quadratic1Y} ${point1X},${point1Y}
                H${topLength}
                Q${quadratic2X},${quadratic2Y} ${point2X},${point2Y}
                V${rightLength}
                Q${quadratic3X},${quadratic3Y} ${point3X},${point3Y}
                H${bottomLength}
                Q${quadratic4X},${quadratic4Y} ${point4X},${point4Y}
                V${startY}
                Z`}
            fill={fill}
            style={style}
            className={className}
            onMouseEnter={() => onHover?.(name)}
            onMouseLeave={() => onHover?.(undefined)}
        />
    );
};

const ActiveBarShape = (props: HoverableBarShapeProps) => {
    const {
        x,
        y,
        width,
        height,
        fill,
        // value,
        payload,
        containerRect,
        valueFormatter,
    } = props;

    const gap = 5;
    const thickness = 3;
    const centerX = x + width / 2;
    const textGap = 12;
    const padding = 16;

    const cos45 = Math.cos(Math.PI / 4); // = Math.sin(Math.PI /4) = 0.707
    const horizontalDir = centerX >= containerRect.width / 2 ? -1 : 1; //index + 1 >= Math.round(totalBars / 2) ? -1 : 1;

    // 0. Starting Point
    const startX = centerX; // Center of the bar
    const startY = y + height + gap; // Bottom of the bar + gap

    // 1. SEGMENT 1
    const seg1Length = 14; // Length of the first segment
    const point1X = startX + seg1Length * cos45 * horizontalDir; // X component of the point seg1Length from startX at 45° angle toward the center
    const point1Y = startY + seg1Length * cos45; // Y component of the point seg1Length from startX at 45° angle

    // 2. SEGMENT 2
    const seg2Length = 20; // Length of the second segment
    const point2X = point1X + seg2Length * horizontalDir; // X component of the 2nd point seg2Length from point1X
    const point2Y = point1Y; // Y component of the 2nd point, we keep it equal to the Y component of point1 to make the segment horizontal (we also could use "H${point2X}" in the path parameters instead)

    // 3. TEXT DISPLAY
    const fullText = payload.name;
    const charWidth = 8.5;

    // Calculate how much space is available for the FIRST line only
    const tipAvailableWidth =
        horizontalDir === 1
            ? containerRect.width - point2X - padding
            : point2X - padding;
    const line1Limit = Math.max(8, Math.floor(tipAvailableWidth / charWidth));

    const wrapIndex = fullText.lastIndexOf(' ', line1Limit);
    const splitPoint = wrapIndex > 0 ? wrapIndex : line1Limit;

    const line1 = fullText.substring(0, splitPoint).trim();
    const line2 = fullText.substring(splitPoint).trim();

    const textX = point2X + 8 * horizontalDir;
    const line1Anchor = horizontalDir === 1 ? 'start' : 'end';

    const value = payload.value;
    const normalizedValue = Array.isArray(value) ? value[1] : value;
    const displayValue = valueFormatter
        ? valueFormatter(normalizedValue)
        : normalizedValue;

    const halfTextWidth = (String(displayValue).length * 8) / 2;

    const isCollidingLeft = centerX - halfTextWidth < padding;
    const isCollidingRight =
        centerX + halfTextWidth > containerRect.width - padding;

    const textPosition = isCollidingLeft
        ? { x: padding, anchor: 'start' as const }
        : isCollidingRight
          ? { x: containerRect.width - padding, anchor: 'end' as const }
          : { x: centerX, anchor: 'middle' as const };

    return (
        <g>
            <text
                x={textPosition.x}
                y={y - textGap}
                textAnchor={textPosition.anchor}
                alignmentBaseline={'central'}
                fill='var(--gray-12)'
                style={{ fontSize: '14px', fontWeight: 'bold' }}
            >
                {displayValue}
            </text>

            <InactiveBarShape {...props} className={styles.active} />

            <path
                d={`M${x},${startY}
                    L${x + width},${startY}`}
                stroke={fill}
                fill='none'
                strokeWidth={thickness}
                strokeLinecap='round'
                strokeLinejoin='round'
            />

            <path
                d={`M${startX},${startY}
                    L${point1X},${point1Y}
                    L${point2X},${point2Y}`}
                stroke={fill}
                fill='none'
                strokeWidth={2}
                strokeLinecap='round'
                strokeLinejoin='round'
            />

            <circle
                cx={point2X}
                cy={point2Y}
                r={2.5}
                fill={fill}
                stroke={'none'}
            />

            <text
                y={point1Y}
                fill={fill}
                fontSize='14px'
                fontWeight='bold'
                dominantBaseline='central'
            >
                <tspan x={textX} textAnchor={line1Anchor}>
                    {line1}
                </tspan>

                {line2 &&
                    (() => {
                        const line2Width = line2.length * charWidth;
                        let line2X = textX;
                        let line2Anchor: 'start' | 'end' = line1Anchor;

                        if (
                            horizontalDir === -1 &&
                            line2X - line2Width < padding
                        ) {
                            line2X = padding;
                            line2Anchor = 'start';
                        } else if (
                            horizontalDir === 1 &&
                            line2X + line2Width > containerRect.width - padding
                        ) {
                            line2X = containerRect.width - padding;
                            line2Anchor = 'end';
                        }

                        return (
                            <tspan
                                x={line2X}
                                dy='1.2em'
                                textAnchor={line2Anchor}
                            >
                                {line2}
                            </tspan>
                        );
                    })()}
            </text>
        </g>
    );
};

const BarShape = (props: HoverableBarShapeProps) => {
    if (props.isActive) return <ActiveBarShape {...props} />;
    return <InactiveBarShape {...props} />;
};

export const BarChart = ({
    data,
    hoveredName,
    onHover,
    valueFormatter,
    ...props
}: BarChartProps) => {
    const [containerRef, containerRect] = useMeasure<HTMLDivElement>();

    return (
        <Box
            ref={containerRef}
            position={'relative'}
            width={'100%'}
            height={'100%'}
            asChild
        >
            <ReBarChart
                data={data}
                tabIndex={-1}
                margin={{ top: 40, bottom: 15, left: 0, right: 0 }}
                responsive
            >
                <XAxis
                    axisLine={{ stroke: 'var(--gray-6)', strokeWidth: 1 }}
                    tick={false}
                    tickLine={false}
                />

                <YAxis hide domain={[0, (dataMax: number) => dataMax * 1.15]} />

                <Bar
                    {...props}
                    spacing={0}
                    shape={(shapeProps) => {
                        return (
                            <BarShape
                                {...shapeProps}
                                onHover={onHover}
                                isActive={
                                    shapeProps.name === hoveredName ||
                                    shapeProps.isActive
                                }
                                containerRect={containerRect}
                                valueFormatter={valueFormatter}
                            />
                        );
                    }}
                    radius={[4, 4, 0, 0]}
                    tabIndex={-1}
                />
            </ReBarChart>
        </Box>
    );
};
